import { useCallback, useMemo, useRef } from 'react';

import { isEqual } from '@Sage/ssm-utils';

import { CHARTS_LIST } from '../constants';
/**
 * Encapsulates snapshot refs and callbacks for ForceBI widget images (download/share).
 * @param {{ selected: number[], widgets: object[], gridUtilsRef: React.MutableRefObject }} params
 * @returns Snapshot API: getSnapshot, deleteSnapshot, getSingleSnapshot, getBulkSnapshots, getExistingSnapshots, updateErrorWidgets, beforeDownloadImages
 */
function useForceBISnapshots({ selected, widgets, gridUtilsRef }) {
    const snapshotsRef = useRef({});
    const errorWidgetsRef = useRef([]);

    const getSnapshot = useCallback(({ id, title, image }) => {
        snapshotsRef.current[id] = { title, image };
    }, []);

    const deleteSnapshot = useCallback((id) => {
        if (snapshotsRef.current?.[id]) {
            delete snapshotsRef.current[id];
        }
    }, []);

    const getSingleSnapshot = useCallback((id) => {
        const snapshot = snapshotsRef.current?.[id] ?? null;
        if (!snapshot) return null;
        return { data: snapshot.image, name: snapshot.title };
    }, []);

    const getBulkSnapshots = useCallback(
        (snapshots) => {
            return Object.entries(snapshots).reduce((obj, [id, snapshot]) => {
                if (selected.includes(parseInt(id, 10))) obj[id] = snapshot;
                return obj;
            }, {});
        },
        [selected],
    );

    const getExistingSnapshots = useCallback(() => {
        return selected.filter((id) => snapshotsRef.current[id]);
    }, [selected]);

    const updateErrorWidgets = useMemo(
        () => ({
            add: (id) => {
                const newId = parseInt(id, 10);
                const current = errorWidgetsRef.current;
                if (current.includes(newId)) return;
                const next = [...current, newId];
                errorWidgetsRef.current = next;
                if (snapshotsRef.current?.[id]) delete snapshotsRef.current[id];
            },
            remove: (id) => {
                const newId = parseInt(id, 10);
                const current = errorWidgetsRef.current;
                if (!current.includes(newId)) return;
                errorWidgetsRef.current = current.filter((x) => x !== newId);
            },
        }),
        [],
    );

    const beforeDownloadImages = useCallback(() => {
        return new Promise((resolve, reject) => {
            const intervalTime = 200;
            const timeout = 6000;
            let elapsedTime = 0;

            const widgetsToDownload = widgets.reduce((arr, current) => {
                const widgetId = parseInt(current.id, 10);
                if (selected.includes(widgetId) && CHARTS_LIST.includes(current.chartType)) {
                    arr.push(widgetId);
                }
                return arr;
            }, []);

            const checkDone = () => {
                const existing = selected.filter((id) => snapshotsRef.current[id]);
                const selectedCharts = widgets.reduce((arr, current) => {
                    const widgetId = parseInt(current.id, 10);
                    if (selected.includes(widgetId) && CHARTS_LIST.includes(current.chartType)) {
                        arr.push(widgetId);
                    }
                    return arr;
                }, []);
                const errorCharts = widgets.reduce((arr, current) => {
                    const widgetId = parseInt(current.id, 10);
                    if (
                        selected.includes(widgetId) &&
                        CHARTS_LIST.includes(current.chartType) &&
                        errorWidgetsRef.current.includes(widgetId)
                    ) {
                        arr.push(widgetId);
                    }
                    return arr;
                }, []);
                const existingAndErrors = [...existing, ...errorCharts].filter((id) =>
                    selectedCharts.includes(id),
                );
                return (
                    isEqual(
                        [...existing].sort((a, b) => a - b),
                        [...widgetsToDownload].sort((a, b) => a - b),
                    ) ||
                    isEqual(
                        [...existingAndErrors].sort((a, b) => a - b),
                        [...selectedCharts].sort((a, b) => a - b),
                    )
                );
            };

            if (checkDone()) {
                resolve();
                return;
            }

            const gridUtils = gridUtilsRef?.current;
            if (!gridUtils?.initHiddenWidgets) {
                reject(new Error('Grid utils not ready'));
                return;
            }

            gridUtils.initHiddenWidgets().then(() => {
                const interval = setInterval(() => {
                    if (checkDone()) {
                        clearInterval(interval);
                        resolve();
                        return;
                    }
                    elapsedTime += intervalTime;
                    if (elapsedTime >= timeout) {
                        clearInterval(interval);
                        reject(new Error('Timeout: snapshots did not complete'));
                    }
                }, intervalTime);
            });
        });
    }, [selected, widgets, gridUtilsRef]);

    return {
        snapshotsRef,
        errorWidgetsRef,
        getSnapshot,
        deleteSnapshot,
        getSingleSnapshot,
        getBulkSnapshots,
        getExistingSnapshots,
        updateErrorWidgets,
        beforeDownloadImages,
    };
}

export default useForceBISnapshots;
